import {
  ContainerProps,
  Flex,
  FormLabel,
  RadioProps,
  useRadio,
  useRadioGroup,
} from '@chakra-ui/react';
import React from 'react';
import { useController } from 'react-hook-form';

interface IRadioButtonProps extends RadioProps {
  styles?: ContainerProps;
}

interface BaseOption {
  value: string;
  label?: string | number;
}

type IOption<T = {}> = BaseOption & T;

interface IRadioButtonFieldProps<T = {}> extends Omit<ContainerProps, 'onChange'> {
  name: string;
  options: IOption<T>[];
  label?: string;
  containerProps?: ContainerProps;
  onChange?: (value: string) => void;
  renderOption?: (option: IOption<T>, isSelected: boolean) => React.ReactNode;
}

const RadioButton: React.FC<IRadioButtonProps> = ({ styles, ...props }) => {
  const { getInputProps, getRadioProps } = useRadio(props);

  const input = getInputProps();
  const checkbox = getRadioProps();

  return (
    <Flex
      alignItems='center'
      as='label'
      flexGrow='1'
    >
      <input {...input} />
      <Flex
        as='span'
        {...checkbox}
        _checked={{
          fontWeight: 'semibold',
          borderColor: 'primary.pure',
        }}
        alignItems='center'
        bg='bg.pure'
        borderRadius='md'
        borderWidth='1px'
        cursor='pointer'
        flexGrow='1'
        justifyContent='center'
        minH={10}
        textTransform='capitalize'
        {...styles}
      >
        {props.children}
      </Flex>
    </Flex>
  );
};

const RadioButtonField = <T extends {} = {}>({
  options,
  name,
  label,
  containerProps,
  onChange,
  renderOption,
  ...props
}: IRadioButtonFieldProps<T>) => {
  const {
    field: { onChange: onFieldChange, value },
  } = useController({
    name,
  });

  const { getRootProps, getRadioProps } = useRadioGroup({
    name,
    defaultValue: value,
    onChange: (value) => {
      onFieldChange(value);
      onChange?.(value);
    },
  });

  const group = getRootProps();

  return (
    <Flex
      {...group}
      w='full'
      flexDir='column'
    >
      {label && <FormLabel htmlFor={name}>{label}</FormLabel>}
      <Flex
        gap='sm'
        justifyContent='space-between'
        {...containerProps}
      >
        {options.map((option) => {
          const radio = getRadioProps({ value: option.value });
          const isSelected = option.value === value;

          return (
            <RadioButton
              key={option.value}
              styles={props}
              {...radio}
            >
              {renderOption ? renderOption(option, isSelected) : option.label}
            </RadioButton>
          );
        })}
      </Flex>
    </Flex>
  );
};

export default RadioButtonField;
